import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "./navbar.css";
import Modal from "./UserFrontEndModal/userFrontEndModal";
import NavLogo from "./logo.jsx";
import classnames from "classnames";
import { useMsal, MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { appRoles, loginRequest } from '../../authConfig';
import accessibility from './accessibility.module.js';

const BREAKPOINT = 1024;
const adminRoles = [appRoles.admin, appRoles.superAdmin];

const Navbar = () => {
    const [menuExpanded, setMenuExpanded] = useState(() =>
        typeof window !== 'undefined'
            ? matchMedia(`(min-width: ${BREAKPOINT}px)`).matches
            : false
    );
    const navbarRef = useRef(null);
    const location = useLocation();
    const { instance, inProgress } = useMsal();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const currentAccount = instance.getActiveAccount();

    const authRequest = {
        ...loginRequest
    };

    // Using useCallback to memoize the authorization check function
    const checkAuthorization = useCallback(async () => {
        try {
            if (currentAccount?.idTokenClaims?.['roles']) {
                const intersection = adminRoles.filter(
                    role => currentAccount.idTokenClaims['roles'].includes(role)
                );
                setIsAuthorized(intersection.length > 0);
            }
        } catch (error) {
            console.error('Authorization check failed:', error);
            setIsAuthorized(false);
        }
    }, [currentAccount]);

    // Handle accessibility focus management
    useEffect(() => {
        const updateFocusTrap = () => {
            if (navbarRef.current) {
                try {
                    accessibility.setKeepFocusInside(
                        navbarRef.current,
                        menuExpanded && window.innerWidth <= BREAKPOINT
                    );
                } catch (error) {
                    console.error('Focus management failed:', error);
                }
            }
        };

        updateFocusTrap();

        const mediaQuery = matchMedia(`(min-width: ${BREAKPOINT}px)`);
        mediaQuery.addEventListener('change', updateFocusTrap);
        return () => mediaQuery.removeEventListener('change', updateFocusTrap);

    }, [menuExpanded]);

    // Handle authorization check
    useEffect(() => {
        checkAuthorization();
    }, [checkAuthorization]);

    // Handle responsive behavior with error handling
    useEffect(() => {
        try {
            const mediaQuery = matchMedia(`(min-width: ${BREAKPOINT}px)`);
            const handleResize = (event) => setMenuExpanded(event.matches);

            mediaQuery.addEventListener('change', handleResize);
            return () => mediaQuery.removeEventListener('change', handleResize);
        } catch (error) {
            console.error('Media query setup failed:', error);
        }
    }, []);

    // Reset menu on location change
    useEffect(() => {
        if (window.innerWidth <= BREAKPOINT) {
            setMenuExpanded(false);
        }
    }, [location]);

    const handleJumpToMain = useCallback((e) => {
        e.preventDefault();
        const sectionElement = document.getElementById("mainContent");
        if (sectionElement) {
            try {
                accessibility.focusAndScrollToView(sectionElement);
            } catch (error) {
                console.error('Jump to main content failed:', error);
                sectionElement.scrollIntoView();
                sectionElement.focus();
            }
        }
    }, []);

    const onClickLogout = useCallback(async (e) => {
        e.preventDefault();
        try {
            if (inProgress === InteractionStatus.None) {
                await instance.logoutRedirect();
            }
        } catch (error) {
            console.error('Logout failed:', error);
        }
    }, [inProgress, instance]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && menuExpanded) {
            setMenuExpanded(false);
        }
    }, [menuExpanded]);

    const renderNavLinks = () => (
        <>
            <a href="/NewTest">New Test</a>
            <MsalAuthenticationTemplate
                interactionType={InteractionType.None}
                authenticationRequest={authRequest}
            >
                {isAuthorized && (
                    <a href="/basicInfo/uploadTest">Upload Test from Spreadsheet</a>
                )}
            </MsalAuthenticationTemplate>
            <a href="/uploadJson">Upload Test from Extension</a>
            <a href="/ManageTest">Manage Tests</a>
            <MsalAuthenticationTemplate
                interactionType={InteractionType.None}
                authenticationRequest={authRequest}
            >
                {isAuthorized && (
                    <a href="/accountOverview">Dashboard</a>
                )}
            </MsalAuthenticationTemplate>
            <MsalAuthenticationTemplate
                interactionType={InteractionType.None}
                authenticationRequest={authRequest}
            >
                {isAuthorized && (
                    <a href="/questions">Edit Survey</a>
                )}
            </MsalAuthenticationTemplate>
            <a href="/" className="logout" onClick={onClickLogout}>Logout</a>
        </>
    );

    return location.pathname === "/" ? (
        <nav ref={navbarRef} id="navbar" aria-label="Primary">
            <a href="/home" id="nav-title">
                <div id="navlogo"><NavLogo /></div>
                <div id="sitetitle">Accessibility Indicators Test</div>
            </a>
        </nav>
    ) : (
        <>
            <nav
                ref={navbarRef}
                id="navbar"
                aria-label="Primary"
                onKeyDown={handleKeyDown}
            >
                <a
                    onClick={handleJumpToMain}
                    href={`${location.pathname}#mainContent`}
                    id="skipLink"
                >
                    Skip to main content
                </a>
                <a href="/home" id="nav-title">
                    <div id="navlogo"><NavLogo /></div>
                    <div id="sitetitle">Accessibility Indicators Test</div>
                </a>
                <div id="navbarcollapse">
                    <button
                        aria-label={`${menuExpanded ? 'Close' : 'Open'} navigation menu`}
                        aria-expanded={menuExpanded}
                        aria-controls="navbar-right"
                        id="drawerbtnup"
                        className={classnames('drawerbtn-up', { 'drawerbtn-down': menuExpanded })}
                        onClick={() => setMenuExpanded(!menuExpanded)}
                    >
                        <div className='chevron'>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" width="30px" height="30px">
                                <path d="M5,17.5 L15,8.75 L25,17.5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                <path d="M5,22.5 L15,15 L25,22.5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                        </div>
                    </button>
                </div>
                <div
                    id="navbar-right"
                    className={menuExpanded ? 'mobile-open' : ''}
                >
                    {renderNavLinks()}
                </div>
            </nav>
            <Modal />
        </>
    );
};

export default Navbar;
